// Reading a price list in the browser (UC-IMPORTS-001, -002; BR-IMPORTS-002, -005): the file's checks that are made before it is
// sent — its size, a workbook or other binary content by its first bytes — its bytes as the base64 the upload carries, its SHA-256,
// and the header and first lines at a separator and header row, read as the Worker reads them (RFC 4180 quoting, a blank header
// cell named by its position, blank lines skipped), so the mapping step shows the columns of another separator or header row
// before anything is sent. The server reads the file again and decides; nothing here is trusted by it.
import { IMPORT_LIMITS, type CsvSeparator } from "@visionx/contracts";

/** Binary content by its first bytes: a ZIP (an `.xlsx` workbook), a PDF, a Windows program, an OLE file (`.xls`), gzip, 7-Zip, an ELF. */
const BINARY_SIGNATURES: readonly (readonly number[])[] = [
  [0x50, 0x4b, 0x03, 0x04], [0x50, 0x4b, 0x05, 0x06], [0x25, 0x50, 0x44, 0x46], [0x4d, 0x5a], [0xd0, 0xcf, 0x11, 0xe0], [0x1f, 0x8b],
  [0x37, 0x7a, 0xbc, 0xaf], [0x7f, 0x45, 0x4c, 0x46],
];
const ZIP = [[0x50, 0x4b, 0x03, 0x04], [0x50, 0x4b, 0x05, 0x06]] as const;
const startsWith = (b: Uint8Array, sig: readonly number[]): boolean => sig.every((v, i) => b[i] === v);

/** Why a chosen file is refused before it is sent (BR-IMPORTS-002; UC-IMPORTS-001 E1, E2): its size, a workbook, or not text. */
export type LocalRefusal = "too_large" | "workbook" | "binary" | "empty";

export function refusalOf(fileName: string, size: number, head: Uint8Array): LocalRefusal | null {
  if (size > IMPORT_LIMITS.fileBytesMax) return "too_large";
  if (size === 0) return "empty";
  if (/\.(xlsx|xlsm|xls)$/i.test(fileName.trim()) || ZIP.some((s) => startsWith(head, s))) return "workbook";
  if (BINARY_SIGNATURES.some((s) => startsWith(head, s))) return "binary";
  return null;
}

/** The file's bytes. */
export async function bytesOf(file: Blob): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === "function") return new Uint8Array(await file.arrayBuffer());
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(new Uint8Array(r.result as ArrayBuffer));
    r.onerror = () => reject(r.error ?? new Error("the file could not be read"));
    r.readAsArrayBuffer(file);
  });
}

/** Standard base64, as the upload carries the file (BR-IMPORTS-003). */
export function base64Of(bytes: Uint8Array): string {
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  return btoa(binary);
}

/** The text of UTF-8 bytes, a byte-order mark dropped; text that is not UTF-8 is read loosely here — the server refuses it. */
export function textOf(bytes: Uint8Array): string {
  const text = new TextDecoder("utf-8").decode(bytes);
  return text.startsWith("﻿") ? text.slice(1) : text;
}

/** The SHA-256 of the bytes in hex, or null where the browser offers no digest. */
export async function sha256Of(bytes: Uint8Array): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  const digest = await subtle.digest("SHA-256", bytes as unknown as ArrayBuffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Every record of the text, RFC 4180: a quoted cell may hold the separator, a doubled quote and a line break. */
function records(text: string, separator: CsvSeparator, upTo: number): string[][] {
  const out: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  let started = false;
  const end = () => { row.push(cell); out.push(row); row = []; cell = ""; started = false; };
  for (let i = 0; i < text.length && out.length < upTo; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else quoted = false; } else cell += ch;
      continue;
    }
    if (ch === '"' && cell.length === 0) { quoted = true; started = true; continue; }
    if (ch === separator) { row.push(cell); cell = ""; started = true; continue; }
    if (ch === "\r" || ch === "\n") { if (ch === "\r" && text[i + 1] === "\n") i++; end(); continue; }
    cell += ch;
    started = true;
  }
  if (out.length < upTo && (started || cell.length > 0 || row.length > 0)) end();
  return out;
}

/** The separator a file most likely uses: the one its first line holds most of, a comma on a tie (as the Worker sniffs it). */
export function sniffSeparator(text: string): CsvSeparator {
  const firstLine = text.slice(0, Math.min(text.length, 4_096)).split(/\r\n|\n|\r/)[0] ?? "";
  const counts = ([",", ";", "\t"] as const).map((s) => [s, firstLine.split(s).length - 1] as const);
  const best = counts.reduce((a, b) => (b[1] > a[1] ? b : a));
  return best[1] > 0 ? best[0] : ",";
}

/** The header's names: each cell trimmed, a blank one named by its position, "Column 6" (BR-IMPORTS-005). */
export const headerNames = (cells: readonly string[]): string[] => cells.map((c, i) => (c.trim() === "" ? `Column ${i + 1}` : c.trim()));

/** A header name as a mapping compares it: case and surrounding spaces ignored (BR-IMPORTS-006). */
export const headerKey = (name: string): string => name.trim().toLowerCase();

/** The header row's names and the first three price lines below it (blank lines skipped), or no header where the file is shorter. */
export function previewAt(text: string, separator: CsvSeparator, headerRow: number): { header: string[]; sample: string[][] } | null {
  const recs = records(text, separator, headerRow + 40);
  const header = recs[headerRow - 1];
  if (!header) return null;
  const sample = recs.slice(headerRow).filter((r) => r.some((c) => c.trim() !== "")).slice(0, 3).map((r) => r.map((c) => c.trim()));
  return { header: headerNames(header), sample };
}

// The text of each file uploaded from this page, by its import, for the mapping step's other separators and header rows. It
// lives only as long as the page: a page opened afresh asks for the file again (UC-IMPORTS-002, open questions).
const texts = new Map<string, { text: string; sha256: string | null }>();
export const keepFileText = (importId: string, text: string, sha256: string | null): void => { texts.set(importId, { text, sha256 }); };
export const fileTextOf = (importId: string): string | null => texts.get(importId)?.text ?? null;
